import { reactKeycloakWebContext } from '@react-keycloak/web/lib/context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import {
  isSessionExpiredError,
  usePrivateRequest,
} from '@/lib/axios/use-private-request';
import { useGetUserApi } from '@/services/agentous-agentic-service';
import userProfileService from '@/services/user-profile-service';
import {
  type ChangePasswordRequest,
  type UpdateAvatarPayload,
  type UpdateUserInfoRequest,
  type UserBasicInfoPayload,
} from '@/types/user';
import { BASE_URL } from '@/utils/api-urls';
import { GET_USER_QUERY } from '@/utils/query-keys';

// Query keys for React Query cache management
export const USER_PROFILE_QUERY_KEYS = {
  all: ['user-profile'] as const,
  userFunctions: () =>
    [...USER_PROFILE_QUERY_KEYS.all, 'user-functions'] as const,
  userProfile: () =>
    [...USER_PROFILE_QUERY_KEYS.all, 'user-profile-data'] as const,
};

/**
 * Hook to fetch the user's profile information
 */
export const useGetUserProfile = <T extends UserBasicInfoPayload>(
  options = {}
) => {
  const keycloakContext = useContext(reactKeycloakWebContext);
  const initialized = keycloakContext?.initialized ?? false;
  const keycloak = keycloakContext?.authClient;
  const getUser = useGetUserApi();

  return useQuery({
    queryKey: [GET_USER_QUERY],
    queryFn: () => getUser<T>(),
    ...options,
    select: data => {
      const {
        userInfo: { dateOfBirth, ...userRest },
        ...others
      } = data;

      const newData = {
        ...others,
        userInfo: {
          ...userRest,
          dateOfBirth: dateOfBirth && new Date(dateOfBirth),
        },
      };
      return newData;
    },
    // Don't retry on session expired errors - user needs to re-authenticate
    retry: (failureCount, error) => {
      if (isSessionExpiredError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    enabled: !!initialized && !!keycloak?.authenticated,
  });
};

/**
 * Hook to fetch user functions/roles from API
 */
export const useGetUserFunctions = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useQuery({
    queryKey: USER_PROFILE_QUERY_KEYS.userFunctions(),
    queryFn: async () => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      const response = await userProfileService.getUserFunctions(
        axiosInstance.current
      );
      return response;
    },
    enabled: !!axiosInstance.current,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to update user information
 */
export const useUpdateUserInfoMutation = () => {
  const queryClient = useQueryClient();
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (payload: UpdateUserInfoRequest) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.updateUserInfo(axiosInstance.current, payload);
    },
    onSuccess: () => {
      // Invalidate user data to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: [GET_USER_QUERY] });
    },
  });
};

/**
 * Hook to update user avatar
 */
export const useUpdateAvatarMutation = () => {
  const queryClient = useQueryClient();
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async ({
      payload,
      onProgress,
    }: {
      payload: UpdateAvatarPayload;
      onProgress?: (progress: number) => void;
    }) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.updateAvatar(
        axiosInstance.current,
        payload,
        onProgress
      );
    },
    onSuccess: () => {
      // Invalidate user data to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to change user password
 */
export const useChangePasswordMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (payload: ChangePasswordRequest) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.changePassword(axiosInstance.current, payload);
    },
  });
};

/**
 * Hook to change user email
 */
export const useChangeEmailMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { email: string; token: string }) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.changeEmail(axiosInstance.current, payload);
    },
    onSuccess: () => {
      // Invalidate auth queries to refresh user session data
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      // Invalidate user profile queries to refresh user data
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.all });
      // Invalidate GET_USER_QUERY to refresh user details
      queryClient.invalidateQueries({ queryKey: [GET_USER_QUERY] });
    },
  });
};

/**
 * Hook to verify email with token
 */
export const useVerifyEmailMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (payload: { token: string }) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.verifyEmail(axiosInstance.current, payload);
    },
  });
};

/**
 * Hook to request email verification
 */
export const useRequestEmailVerificationMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.requestEmailVerification(
        axiosInstance.current,
        payload
      );
    },
  });
};

/**
 * Hook to request token for email/password changes
 */
export const useRequestTokenMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async () => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.requestToken(axiosInstance.current);
    },
  });
};

/**
 * Hook to validate token for registered email
 */
export const useValidateTokenMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (token: string) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.validateToken(axiosInstance.current, token);
    },
  });
};

/**
 * Hook to request token for unregistered email
 */
export const useRequestUnregisteredEmailTokenMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (email: string) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.requestUnregisteredEmailToken(
        axiosInstance.current,
        email
      );
    },
  });
};

/**
 * Hook to verify token for unregistered email
 */
export const useVerifyUnregisteredEmailTokenMutation = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  return useMutation({
    mutationFn: async (token: string) => {
      if (!axiosInstance.current) {
        throw new Error('Axios instance not ready');
      }
      return userProfileService.verifyUnregisteredEmailToken(
        axiosInstance.current,
        token
      );
    },
  });
};

// Legacy exports for backward compatibility
export const useUpdateUserInfo = () => {
  const mutation = useUpdateUserInfoMutation();
  return mutation.mutateAsync;
};

export const useUpdateAvatar = () => {
  const mutation = useUpdateAvatarMutation();
  return mutation.mutateAsync;
};
